import React from "react";
import {
  Grid,
  Switch,
  FormGroup,
  FormLabel,
  FormControl,
  FormControlLabel,
  Select,
  Chip,
  MenuItem,
  Input,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import WebMAPToolTip from "components/WebMAPToolTip";

const ChipsWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
}));

class SearchSettings extends React.PureComponent {
  state = {
    showSearchSourcesFilter: this.props.searchSources.length > 0 ? true : false,
  };

  localUpdateSearchOptions = (name, value) => {
    const { searchOptions } = this.props;
    // Send the new values up to the Search component's state
    this.props.updateSearchOptions({ ...searchOptions, [name]: value });
  };

  render() {
    const { searchOptions, searchSources, searchModel } = this.props;

    const enabledSearchOptions = Array.isArray(this.props.enabledSearchOptions)
      ? this.props.enabledSearchOptions
      : false;

    const showSearchInVisibleLayers =
      !enabledSearchOptions ||
      enabledSearchOptions.includes("searchInVisibleLayers");
    const showWildcardAtStart =
      !enabledSearchOptions || enabledSearchOptions.includes("wildcardAtStart");
    const showWildcardAtEnd =
      !enabledSearchOptions || enabledSearchOptions.includes("wildcardAtEnd");
    const showMatchCase =
      !enabledSearchOptions || enabledSearchOptions.includes("matchCase");
    const showActiveSpatialFilter =
      !enabledSearchOptions ||
      enabledSearchOptions.includes("activeSpatialFilter");
    const showEnableLabelOnHighlight =
      !enabledSearchOptions ||
      enabledSearchOptions.includes("enableLabelOnHighlight");

    return (
      <Grid container spacing={2} direction="column">
        <Grid item xs>
          <FormControl component="fieldset">
            <FormLabel component="legend">Общи настройки за търсене</FormLabel>
            <FormGroup>
              <WebMAPToolTip title="Включете, за да изберете в кои източници на данни ще се извършва търсенето. Ако превключвателят е в изключено положение, търсенето ще се извършва във всички налични източници за търсене.">
                <FormControlLabel
                  label="Ограничи източници за търсене"
                  control={
                    <Switch
                      checked={this.state.showSearchSourcesFilter}
                      onChange={(e) => {
                        // Pull out the new value
                        const showSearchSourcesFilter = e.target.checked;

                        // Set state to reflect in Switch's UI
                        this.setState({
                          showSearchSourcesFilter,
                        });

                        // Now, if user has turned off this setting, ensure
                        // that we also clean all search sources
                        if (showSearchSourcesFilter === false)
                          this.props.setSearchSources([]);
                      }}
                      color="primary"
                    />
                  }
                />
              </WebMAPToolTip>
              {this.state.showSearchSourcesFilter && (
                <Grid container spacing={2}>
                  <Grid item xs>
                    <Select
                      fullWidth
                      labelId="demo-mutiple-chip-label"
                      multiple
                      value={searchSources}
                      onChange={(event) =>
                        this.props.setSearchSources(event.target.value)
                      }
                      input={<Input id="select-multiple-chip" />}
                      renderValue={(selected) => (
                        <ChipsWrapper>
                          {selected.map((option) => (
                            <Chip
                              key={option.id}
                              label={option.caption}
                              sx={{ margin: 0.25 }}
                            />
                          ))}
                        </ChipsWrapper>
                      )}
                    >
                      {searchModel
                        .getSources()
                        .sort((a, b) => a.caption.localeCompare(b.caption))
                        .map((source) => (
                          <MenuItem key={source.id} value={source}>
                            {source.caption}
                          </MenuItem>
                        ))}
                    </Select>
                  </Grid>
                </Grid>
              )}
            </FormGroup>
            {showSearchInVisibleLayers && (
              <FormGroup>
                <WebMAPToolTip title="Ако е активно, търсенето ще се извършва в слоеве, които са настроени за търсенеи които са видими.">
                  <FormControlLabel
                    label="Търси само във видимите слоеве"
                    control={
                      <Switch
                        checked={searchOptions.searchInVisibleLayers}
                        onChange={() => {
                          this.localUpdateSearchOptions(
                            "searchInVisibleLayers",
                            !searchOptions.searchInVisibleLayers
                          );
                        }}
                        color="primary"
                      />
                    }
                  />
                </WebMAPToolTip>
              </FormGroup>
            )}
          </FormControl>
        </Grid>

        <Grid item xs>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              Настройки за текстово търсене
            </FormLabel>
            <FormGroup>
              {showWildcardAtStart && (
                <WebMAPToolTip
                  disableInteractive
                  title="Ако е активно, търсене за 'път' ще даде резултати и за например 'главен път'."
                >
                  <FormControlLabel
                    label="Wildcard преди"
                    control={
                      <Switch
                        checked={searchOptions.wildcardAtStart}
                        onChange={() =>
                          this.localUpdateSearchOptions(
                            "wildcardAtStart",
                            !searchOptions.wildcardAtStart
                          )
                        }
                        color="primary"
                      />
                    }
                  />
                </WebMAPToolTip>
              )}
              {showWildcardAtEnd && (
                <WebMAPToolTip
                  disableInteractive
                  title="Ако е активно, търсене за 'път' ще даде резултати и за например 'пътя'."
                >
                  <FormControlLabel
                    label="Wildcard след"
                    control={
                      <Switch
                        checked={searchOptions.wildcardAtEnd}
                        onChange={() =>
                          this.localUpdateSearchOptions(
                            "wildcardAtEnd",
                            !searchOptions.wildcardAtEnd
                          )
                        }
                        color="primary"
                      />
                    }
                  />
                </WebMAPToolTip>
              )}
              {showMatchCase && (
                <WebMAPToolTip
                  disableInteractive
                  title="Ако е активно, търсене за 'а' няма да даде резултати за 'А'. Деактивирайте, за да търсите независимо от малки/големи букви."
                >
                  <FormControlLabel
                    label="Чувствителност към регистъра"
                    control={
                      <Switch
                        checked={searchOptions.matchCase}
                        onChange={() =>
                          this.localUpdateSearchOptions(
                            "matchCase",
                            !searchOptions.matchCase
                          )
                        }
                        color="primary"
                      />
                    }
                  />
                </WebMAPToolTip>
              )}
              {showActiveSpatialFilter && (
                <Grid item xs>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      Пространствени настройки за търсене
                    </FormLabel>
                    <FormGroup>
                      <WebMAPToolTip
                        disableInteractive
                        title="Ако е активно, целият обект (например имот) трябва да се вмести в областта за търсене, за да бъде включен в резултата. Ако е неактивно, достатъчно е само малка част от обекта да се вмести в или да докосва областта за търсене."
                      >
                        <FormControlLabel
                          label="Изисквай целият обект да се вмести в областта за търсене"
                          control={
                            <Switch
                              checked={
                                searchOptions.activeSpatialFilter === "within"
                              }
                              onChange={() =>
                                this.localUpdateSearchOptions(
                                  "activeSpatialFilter",
                                  searchOptions.activeSpatialFilter ===
                                    "intersects"
                                    ? "within"
                                    : "intersects"
                                )
                              }
                              color="primary"
                            />
                          }
                        />
                      </WebMAPToolTip>
                    </FormGroup>
                  </FormControl>
                </Grid>
              )}
              {showEnableLabelOnHighlight && (
                <Grid item xs>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      Показване на резултати
                    </FormLabel>
                    <FormGroup>
                      <WebMAPToolTip
                        disableInteractive
                        title="Ако е активно, етикет ще се покаже в картата до маркирания резултат от търсенето"
                      >
                        <FormControlLabel
                          label="Покажи текстов етикет в картата"
                          control={
                            <Switch
                              checked={searchOptions.enableLabelOnHighlight}
                              onChange={() =>
                                this.localUpdateSearchOptions(
                                  "enableLabelOnHighlight",
                                  !searchOptions.enableLabelOnHighlight
                                )
                              }
                              color="primary"
                            />
                          }
                        />
                      </WebMAPToolTip>
                    </FormGroup>
                  </FormControl>
                </Grid>
              )}
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
    );
  }
}

export default SearchSettings;
